import L from 'leaflet';
import type { MapProperty, MapTenant } from './types';

const PIN_COLORS: Record<string, string> = {
  listed: '#16a34a', under_contract: '#2563eb', prospect: '#d97706', sold: '#6b7280',
};
export function pinColor(status?: string): string { return PIN_COLORS[status || ''] || '#4b5563'; }
export { PIN_COLORS };

function label(p: MapProperty): string {
  if (p.price) return p.price >= 1_000_000 ? '$' + (p.price / 1_000_000).toFixed(1) + 'M'
    : p.price >= 1_000 ? '$' + Math.round(p.price / 1_000) + 'K' : '$' + p.price;
  if (p.building_size) return p.building_size >= 1000 ? Math.round(p.building_size / 1000) + 'K SF' : p.building_size + ' SF';
  return '';
}

// Property marker: a Zillow-style price/size bubble (falls back to a dot when
// neither price nor size is known). Clustering collapses these into counts.
// `selected` = in the comp tray (purple ring); `active` = hovered/flown-to (scale up).
// Distress level from the merged loan summary: past-due maturity is the hottest lead.
export function distressLevel(p: MapProperty): 'past_due' | 'soon' | null {
  if (!p.loan) return null;
  const m = p.loan.maturity_date;
  if (m && new Date(m + 'T00:00:00') < new Date()) return 'past_due';
  return 'soon';
}

export function propertyBubbleIcon(p: MapProperty, opts: { active?: boolean; selected?: boolean; distress?: 'past_due' | 'soon' } = {}): L.DivIcon {
  const color = pinColor(p.pipeline_status || p.status);
  const text = label(p);
  // Rounded ring via box-shadow so it follows the bubble's border-radius.
  const shadows = [];
  if (opts.distress) shadows.push(`0 0 0 3px ${opts.distress === 'past_due' ? '#dc2626' : '#f59e0b'}`);
  else if (opts.selected) shadows.push('0 0 0 3px #7c3aed');
  if (opts.distress && opts.selected) shadows.push('0 0 0 5px #7c3aed');
  shadows.push(opts.active ? '0 2px 10px rgba(0,0,0,.45)' : '0 1px 4px rgba(0,0,0,.35)');
  const style = `box-shadow:${shadows.join(',')};${opts.active ? 'transform:scale(1.12);z-index:1000;' : ''}`;
  if (!text) {
    return L.divIcon({
      className: '', iconSize: [18, 18], iconAnchor: [9, 9],
      html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;${style}"></div>`,
    });
  }
  // Zillow/CoStar-style price pill with a small pointer tail so it reads as a
  // placed pin. Centered horizontally above the point; the tail tip lands on the
  // coordinate (iconAnchor [0,0] + translate(-50%, -100% - tail)).
  return L.divIcon({
    className: '', iconSize: [0, 0], iconAnchor: [0, 0],
    html:
      `<div style="position:absolute;left:0;top:0;transform:translate(-50%,calc(-100% - 6px));${opts.active ? 'z-index:1000;' : ''}">` +
        `<div style="position:relative;display:inline-block;white-space:nowrap;background:${color};color:#fff;font:600 12px/1 ui-sans-serif,system-ui;padding:5px 8px;border-radius:10px;border:1.5px solid #fff;${style}">${text}` +
          `<span style="position:absolute;left:50%;top:100%;margin-top:-1px;transform:translateX(-50%);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:6px solid ${color};"></span>` +
        `</div>` +
      `</div>`,
  });
}

// Trade-area / ring center marker: a purple target dot that makes the subject
// point obvious on the map.
export const ringCenterIcon = L.divIcon({
  className: '',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: `<div style="width:20px;height:20px;border-radius:50%;background:#7c3aed;border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;"><div style="width:6px;height:6px;border-radius:50%;background:#fff;"></div></div>`,
});

// Cluster bubble: a clean charcoal circle with the grouped count, sized by
// magnitude with a soft halo, so it reads as "N pins here" instead of the
// unstyled bare number the default markercluster renders. Shares the price-pin
// color family for cohesion; the circular shape keeps it distinct from the
// pill-shaped price pins. Counts >= 1,000 abbreviate to "k".
export function clusterIcon(cluster: { getChildCount: () => number }): L.DivIcon {
  const n = cluster.getChildCount();
  const size = n < 10 ? 32 : n < 100 ? 40 : n < 500 ? 48 : 56;
  const fontPx = n < 100 ? 12 : n < 500 ? 13 : 14;
  const text = n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'k' : String(n);
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html:
      `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#374151;color:#fff;` +
      `font:700 ${fontPx}px/1 ui-sans-serif,system-ui;display:flex;align-items:center;justify-content:center;` +
      `border:2px solid #fff;box-shadow:0 0 0 5px rgba(55,65,81,.16),0 2px 6px rgba(0,0,0,.35);">${text}</div>`,
  });
}

/**
 * Tenant prospects: months until the lease expires drives the pin colour, so a glance shows who
 * to call. ROBERT'S OWN THREE SEGMENTS and his own colour names (his brief, 2026-09-15):
 * red = URGENT (0-6mo), orange = PRIME (6-12mo), yellow = EARLY (12-18mo), teal = beyond/unknown.
 *
 * ⛔⛔ IT WAS <=12 RED / <=24 AMBER, which crossed two of his segments and matched neither the
 *    radar nor the CRM tag. ⚠️ THE BOUNDS BELOW ARE A COPY of the backend's `LEASE_URGENCY_BANDS`
 *    because the frontend cannot import backend source (it builds green locally and RED on
 *    Vercel - trap 20). **A backend guard, `leaseBandsMatchFrontend`, reads THIS FILE as text and
 *    fails if the two ever disagree.** Change one, change both.
 */
export const TENANT_EXPIRY_BAND_MONTHS = { urgent: 6, prime: 12, early: 18 } as const;

export function tenantExpiryColor(iso?: string | null): string {
  if (!iso) return '#0d9488';
  const months = (new Date(iso + 'T00:00:00').getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44);
  if (months < TENANT_EXPIRY_BAND_MONTHS.urgent) return '#dc2626';  // URGENT
  if (months < TENANT_EXPIRY_BAND_MONTHS.prime) return '#f97316';   // PRIME
  if (months < TENANT_EXPIRY_BAND_MONTHS.early) return '#eab308';   // EARLY
  return '#0d9488';
}

// Tenant prospect marker: a teardrop pin (distinct from the property price pills
// and permit diamonds) colored by lease-expiration urgency. Anchored at the tip.
export function tenantIcon(t: MapTenant, opts: { active?: boolean } = {}): L.DivIcon {
  const color = tenantExpiryColor(t.lease_expiration);
  return L.divIcon({
    className: '', iconSize: [24, 30], iconAnchor: [12, 29],
    html:
      `<div style="transform:scale(${opts.active ? 1.15 : 1});transform-origin:bottom center;` +
      `${opts.active ? 'z-index:1000;' : ''}filter:drop-shadow(0 2px 3px rgba(0,0,0,.4));">` +
        `<svg width="24" height="30" viewBox="0 0 24 30" xmlns="http://www.w3.org/2000/svg">` +
          `<path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 18 12 18s12-10 12-18C24 5.4 18.6 0 12 0z" fill="${color}" stroke="#fff" stroke-width="2"/>` +
          `<circle cx="12" cy="12" r="4" fill="#fff"/>` +
        `</svg>` +
      `</div>`,
  });
}

// A 16px diamond centered in a 28px transparent hit box, so permits are easy to
// click (the old 12px diamond was too small to hit reliably).
export function permitDiamondIcon(industrial: boolean): L.DivIcon {
  const bg = industrial ? '#dc2626' : '#f59e0b';
  return L.divIcon({
    className: '', iconSize: [28, 28], iconAnchor: [14, 14],
    html: `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;cursor:pointer;">`
      + `<div style="width:16px;height:16px;background:${bg};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45);transform:rotate(45deg);"></div>`
      + `</div>`,
  });
}
